import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';

export interface Todo {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  category: 'transport' | 'accommodation' | 'activities' | 'food' | 'shopping' | 'documents' | 'general';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  groupId?: string;
  travelId?: string;
  assignedTo?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    username: string;
    display_name: string;
  };
}

interface TodosState {
  todos: Todo[];
  loading: boolean;
  error: string | null;
}

const initialState: TodosState = {
  todos: [],
  loading: false,
  error: null,
};

// 数据转换函数：将数据库字段转换为前端接口字段
const transformDatabaseToTodo = (dbData: any): Todo => ({
  id: dbData.id,
  title: dbData.title,
  description: dbData.description,
  completed: dbData.completed,
  category: dbData.category,
  priority: dbData.priority,
  dueDate: dbData.due_date,
  groupId: dbData.group_id,
  travelId: dbData.travel_id,
  assignedTo: dbData.assigned_to,
  createdBy: dbData.created_by,
  createdAt: dbData.created_at,
  updatedAt: dbData.updated_at,
  creator: dbData.creator, // 添加创建者信息
});

// Async thunks
export const fetchGroupTodos = createAsyncThunk(
  'todos/fetchGroupTodos',
  async (groupId: string) => {
    const { data, error } = await supabase
      .from('travel_todos')
      .select('*')
      .eq('group_id', groupId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // 获取所有创建者ID
    const creatorIds = [...new Set(data?.map(todo => todo.created_by).filter(Boolean))];
    
    // 批量获取创建者信息
    let creators: any[] = [];
    if (creatorIds.length > 0) {
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, username, display_name')
        .in('id', creatorIds);
      creators = profilesData || [];
    }
    
    // 将创建者信息映射到todos
    const todosWithCreators = (data || []).map(todo => ({
      ...todo,
      creator: creators.find(creator => creator.id === todo.created_by)
    }));
    
    return todosWithCreators.map(transformDatabaseToTodo);
  }
);

export const createTodo = createAsyncThunk(
  'todos/createTodo',
  async (todoData: Omit<Todo, 'id' | 'createdAt' | 'updatedAt'>) => {
    const { data, error } = await supabase
      .from('travel_todos')
      .insert([{
        title: todoData.title,
        description: todoData.description,
        completed: todoData.completed,
        category: todoData.category,
        priority: todoData.priority,
        group_id: todoData.groupId,
        travel_id: todoData.travelId,
        assigned_to: todoData.assignedTo,
        created_by: todoData.createdBy,
        due_date: todoData.dueDate,
      }])
      .select()
      .single();

    if (error) throw error;
    
    // 获取创建者信息
    const { data: creatorData } = await supabase
      .from('profiles')
      .select('id, username, display_name')
      .eq('id', todoData.createdBy)
      .single();
    
    // 添加创建者信息到返回的数据
    const todoWithCreator = {
      ...data,
      creator: creatorData
    };
    
    return transformDatabaseToTodo(todoWithCreator);
  }
);

export const updateTodo = createAsyncThunk(
  'todos/updateTodo',
  async ({ id, updates }: { id: string; updates: Partial<Todo> }) => {
    const updateData: any = {};
    
    // 只添加定义的字段，并转换为数据库字段名
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.completed !== undefined) updateData.completed = updates.completed;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.groupId !== undefined) updateData.group_id = updates.groupId;
    if (updates.travelId !== undefined) updateData.travel_id = updates.travelId;
    if (updates.assignedTo !== undefined) updateData.assigned_to = updates.assignedTo;
    if (updates.dueDate !== undefined) updateData.due_date = updates.dueDate;
    
    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('travel_todos')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    // 获取创建者信息
    const { data: creatorData } = await supabase
      .from('profiles')
      .select('id, username, display_name')
      .eq('id', data.created_by)
      .single();
    
    // 添加创建者信息到返回的数据
    const todoWithCreator = {
      ...data,
      creator: creatorData
    };
    
    return transformDatabaseToTodo(todoWithCreator);
  }
);

export const completeTodo = createAsyncThunk(
  'todos/completeTodo',
  async (id: string) => {
    const { data, error } = await supabase
      .from('travel_todos')
      .update({ 
        completed: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    // 获取创建者信息
    const { data: creatorData } = await supabase
      .from('profiles')
      .select('id, username, display_name')
      .eq('id', data.created_by)
      .single();
    
    // 添加创建者信息到返回的数据
    const todoWithCreator = {
      ...data,
      creator: creatorData
    };
    
    return transformDatabaseToTodo(todoWithCreator);
  }
);

export const deleteTodo = createAsyncThunk(
  'todos/deleteTodo',
  async (id: string) => {
    const { error } = await supabase
      .from('travel_todos')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return id;
  }
);

const todosSlice = createSlice({
  name: 'todos',
  initialState,
  reducers: {
    clearTodos: (state) => {
      state.todos = [];
    },
    setTodos: (state, action: PayloadAction<Todo[]>) => {
      state.todos = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch todos
      .addCase(fetchGroupTodos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupTodos.fulfilled, (state, action) => {
        state.loading = false;
        state.todos = action.payload;
      })
      .addCase(fetchGroupTodos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '獲取待辦事項失敗';
      })
      // Create todo
      .addCase(createTodo.fulfilled, (state, action) => {
        state.todos.unshift(action.payload);
      })
      // Update todo
      .addCase(updateTodo.fulfilled, (state, action) => {
        const index = state.todos.findIndex(todo => todo.id === action.payload.id);
        if (index !== -1) {
          state.todos[index] = action.payload;
        }
      })
      // Complete todo
      .addCase(completeTodo.fulfilled, (state, action) => {
        const index = state.todos.findIndex(todo => todo.id === action.payload.id);
        if (index !== -1) {
          state.todos[index] = action.payload;
        }
      })
      // Delete todo
      .addCase(deleteTodo.fulfilled, (state, action) => {
        state.todos = state.todos.filter(todo => todo.id !== action.payload);
      });
  },
});

export const { clearTodos, setTodos } = todosSlice.actions;
export default todosSlice.reducer;
